import {
  EventType,
  type AuthenticationResult,
  type PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { createMsalClient } from "@/config/msalConfig";
import { loadRuntimeConfig } from "@/config/runtimeConfig";
import { persistAuthError } from "@/utils/samlDiagnostic";

type Status =
  | { state: "loading" }
  | { state: "ready"; client: PublicClientApplication }
  | { state: "error"; message: string };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadRuntimeConfig();
        const client = createMsalClient();
        await client.initialize();
        try {
          await client.handleRedirectPromise();
        } catch (redirectErr) {
          // A SAML / AADSTS failure (e.g. AADSTS5000819) will reject
          // handleRedirectPromise with an MSAL AuthError. Persist the
          // full error so the Tokens page diagnostic can render it.
          persistAuthError(redirectErr);
          console.error("[auth] redirect error", redirectErr);
        }

        // Make sure we always have an active account after a refresh.
        if (!client.getActiveAccount()) {
          const accounts = client.getAllAccounts();
          if (accounts.length > 0) client.setActiveAccount(accounts[0]);
        }

        client.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          ) {
            const payload = event.payload as AuthenticationResult | undefined;
            if (payload?.account) client.setActiveAccount(payload.account);
          }
          // Capture login / token / SSO / logout failures so the Tokens
          // page SAML email diagnostic can show the latest AADSTS code,
          // Trace ID and Correlation ID.
          if (
            event.eventType === EventType.LOGIN_FAILURE ||
            event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            event.eventType === EventType.SSO_SILENT_FAILURE ||
            event.eventType === EventType.LOGOUT_FAILURE
          ) {
            persistAuthError(event.error);
          }
        });

        if (!cancelled) setStatus({ state: "ready", client });
      } catch (err) {
        console.error("[auth] bootstrap failed", err);
        if (!cancelled) {
          setStatus({
            state: "error",
            message:
              err instanceof Error ? err.message : "Authentication bootstrap failed",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const content = useMemo(() => {
    if (status.state === "loading") return <BootstrapSplash label="Loading…" />;
    if (status.state === "error") return <BootstrapError message={status.message} />;
    return <MsalProvider instance={status.client}>{children}</MsalProvider>;
  }, [status, children]);

  return content;
}

function BootstrapSplash({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex items-center gap-3 text-slate-600">
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        {label}
      </div>
    </div>
  );
}

function BootstrapError({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md rounded-tile border border-red-200 bg-white p-6 shadow-tile">
        <h1 className="text-lg font-semibold text-red-700">Portal unavailable</h1>
        <p className="mt-2 text-sm text-slate-600">
          The portal could not bootstrap the authentication client. Please try
          again in a moment or contact your administrator.
        </p>
        <pre className="mt-3 max-h-40 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
          {message}
        </pre>
      </div>
    </div>
  );
}
